import { diffCatalogue, slugOf, type ChangedItem, type Draft } from './catalogue';
import type { components } from '../api/generated/entitlements';

type Item = components['schemas']['CatalogueItemView'];

export type PackagingChange =
  | { kind: 'tier-added'; tier: string } | { kind: 'module-added'; module: string } | { kind: 'offer-added'; offer: string }
  | { kind: 'feature-introduced'; feature: string }
  | { kind: 'feature-granted'; tier: string; feature: string } | { kind: 'feature-revoked'; tier: string; feature: string }
  | { kind: 'module-feature-added'; module: string; feature: string } | { kind: 'module-feature-removed'; module: string; feature: string }
  | { kind: 'module-requirement-changed'; module: string; from: string | null; to: string | null }
  | { kind: 'tier-retired'; tier: string } | { kind: 'tier-restored'; tier: string }
  | { kind: 'module-retired'; module: string } | { kind: 'module-restored'; module: string }
  | { kind: 'offer-retired'; offer: string } | { kind: 'offer-changed'; offer: string };

function summariseTier(slug: string, c: ChangedItem, baselineTierFeatures: Set<string>): PackagingChange[] {
  const changes: PackagingChange[] = [];
  if (!c.base) changes.push({ kind: 'tier-added', tier: slug });
  if (c.base?.status === 'retired' && c.draftBody.status !== 'retired') changes.push({ kind: 'tier-restored', tier: slug });
  if (c.base?.status !== 'retired' && c.draftBody.status === 'retired') changes.push({ kind: 'tier-retired', tier: slug });
  const before = new Set(c.base?.features ?? []);
  const after = new Set(c.draftBody.features ?? []);
  for (const f of after) if (!before.has(f)) {
    if (!baselineTierFeatures.has(f)) changes.push({ kind: 'feature-introduced', feature: f });
    changes.push({ kind: 'feature-granted', tier: slug, feature: f });
  }
  for (const f of before) if (!after.has(f)) changes.push({ kind: 'feature-revoked', tier: slug, feature: f });
  return changes;
}

function summariseModule(slug: string, c: ChangedItem): PackagingChange[] {
  const changes: PackagingChange[] = [];
  if (!c.base) changes.push({ kind: 'module-added', module: slug });
  if (c.base?.status === 'retired' && c.draftBody.status !== 'retired') changes.push({ kind: 'module-restored', module: slug });
  if (c.base?.status !== 'retired' && c.draftBody.status === 'retired') changes.push({ kind: 'module-retired', module: slug });
  const before = new Set(c.base?.features ?? []);
  const after = new Set(c.draftBody.features ?? []);
  for (const f of after) if (!before.has(f)) changes.push({ kind: 'module-feature-added', module: slug, feature: f });
  for (const f of before) if (!after.has(f)) changes.push({ kind: 'module-feature-removed', module: slug, feature: f });
  const from = c.base?.requiresTier ?? null;
  const to = c.draftBody.requiresTier ?? null;
  if (from !== to) changes.push({ kind: 'module-requirement-changed', module: slug, from, to });
  return changes;
}

function summariseOffer(slug: string, c: ChangedItem): PackagingChange[] {
  if (!c.base) return [{ kind: 'offer-added', offer: slug }];
  if (c.draftBody.availability === 'retired' && c.base.availability !== 'retired') return [{ kind: 'offer-retired', offer: slug }];
  return [{ kind: 'offer-changed', offer: slug }];
}

export type RetireWarning = {
  retired: string;
  referencedBy: string;
  relation: 'requiresTier' | 'confers' | 'sole-grantor';
  feature?: string;
};

// Modules whose requiresTier points at a now-retired tier.
function requiresTierWarnings(baseline: Item[], retiredTiers: Set<string>): RetireWarning[] {
  return baseline.flatMap((i) =>
    i.type === 'module' && i.requiresTier && retiredTiers.has(i.requiresTier)
      ? [{ retired: `tier:${i.requiresTier}`, referencedBy: i.id, relation: 'requiresTier' as const }]
      : []);
}

// Offers that still confer a now-retired tier or module (EIT-79: the trial becomes unactionable).
function confersWarnings(baseline: Item[], isRetired: (kind: string, slug: string) => boolean): RetireWarning[] {
  return baseline
    .filter((i) => i.type === 'trial-offer')
    .flatMap((i) => (i.confers ?? [])
      .filter((c) => c.slug && isRetired(c.kind ?? '', c.slug))
      .map((c) => ({ retired: `${c.kind}:${c.slug}`, referencedBy: i.id, relation: 'confers' as const })));
}

// Features granted only by a now-retired tier — unobtainable for new orgs.
function soleGrantorWarnings(draft: Draft): RetireWarning[] {
  const activeFeatures = new Set(draft.tiers.filter((t) => t.status !== 'retired').flatMap((t) => t.features));
  return draft.tiers
    .filter((t) => t.status === 'retired')
    .flatMap((t) => t.features
      .filter((f) => !activeFeatures.has(f))
      .map((f) => ({ retired: `tier:${t.slug}`, referencedBy: `feature:${f}`, relation: 'sole-grantor' as const, feature: f })));
}

// Non-blocking warnings for retiring an item that something still points at. Scans ALL baseline
// items (not just the changed set) — an unedited module requiring a now-retired tier is exactly
// the case worth surfacing.
export function retireWarnings(draft: Draft, baseline: Item[]): RetireWarning[] {
  const retiredTiers = new Set(draft.tiers.filter((t) => t.status === 'retired').map((t) => t.slug));
  const retiredModules = new Set(draft.modules.filter((m) => m.status === 'retired').map((m) => m.slug));
  const isRetired = (kind: string, slug: string) =>
    (kind === 'tier' && retiredTiers.has(slug)) || (kind === 'module' && retiredModules.has(slug));
  return [
    ...requiresTierWarnings(baseline, retiredTiers),
    ...confersWarnings(baseline, isRetired),
    ...soleGrantorWarnings(draft),
  ];
}

// Iterates the same diffCatalogue the writes use — so every write has a summary record and
// vice versa. A record per field a body can carry.
export function summarise(draft: Draft, baseline: Item[]): PackagingChange[] {
  const baselineTierFeatures = new Set(baseline.filter((i) => i.type === 'tier').flatMap((t) => t.features ?? []));
  const changes: PackagingChange[] = [];
  for (const c of diffCatalogue(draft, baseline)) {
    const slug = slugOf(c.id);
    if (c.kind === 'tier') changes.push(...summariseTier(slug, c, baselineTierFeatures));
    else if (c.kind === 'module') changes.push(...summariseModule(slug, c));
    else changes.push(...summariseOffer(slug, c));
  }
  return changes;
}
